import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, Modal, Tabs, Tooltip } from 'antd';
import AddNewSoilProfile from '../AddNewSoilProfile';
import './index.less';
import SoilDropDown from '../DropDown';
import { WarningOutlined } from '@ant-design/icons';
import {
    getUserList,
    soilProfileAddNewProfile,
    soilProfileGetExistSourceLink,
    soilProfileGetExtObjects,
    soilProfileGetFieldType,
    soilProfileGetMethodList,
    soilProfileGetValiadateStatus,
} from '@/util/Request';
import {
    updateSelectEstimationSystem,
    updateObjectType,
    updateSelectObjectType,
    updateObjectID,
    updateFieldTypes,
    updateSelectPublisher,
    updateEcoSystems,
    updateEstimationMethods,
    updateTheFieldRequiredCheckModel,
    resetTheSoilCreatProfiles,
    updateCoordinate,
} from '@/redux/reducer/SoilCreatProfiles';
import { RootState } from '@/redux';
import { useDispatch, useSelector } from 'react-redux';
import { getUserListAction } from '@/redux/reducer/userManagement';
import { ISoilProfileAddProfileReqModel, IUserManagementSearchModel } from '@/util/Request/requestType';
import SoilButton from '@/components/SoilButton';
import { updateShowLoading, updateSuccessMessage } from '@/redux/reducer/common';
import { titleCase } from '@/util/commonFunction';
import { updateValidateStatus } from '@/redux/reducer/soilProfile';
import { SoilCloseIcon } from '@/resource/IconComponentSets';

const { confirm } = Modal;

interface IProps {
    visible: boolean
    handleCancel: () => void
    handleOk: () => void
    addSuccessCallBack: () => void
}

export default function SoilProfileCreateView({
    visible,
    handleCancel,
    addSuccessCallBack
}: IProps) {
    const dispatch = useDispatch();

    // SECTION: state define area
    const [userDisplayNameList, setUserDisplayNameList] = useState<string[]>([]);
    const [estimationMethodsDisplayName, setEstimationMethodsDisplayName] = useState<string[]>();
    const [objTypes, setObjTypes] = useState<string[]>();
    const [currentPage, setCurrentPage] = useState<string>('0');
    const [saveLock, setSaveLock] = useState(false);
    const [fieldCheckResult, setFieldCheckResult] = useState('Name coordinate layer-name start-depth end-depth estimation-method field is required!!');

    // SECTION: get target redux value areaa
    const isAuthenticate = useSelector((state: RootState) => state.soilUser.isAuthenticate);
    const selectEcoSystem = useSelector((state: RootState) => state.soilprofileCreateData.selectEcoSystem);
    const objectTypes = useSelector((state: RootState) => state.soilprofileCreateData.objectTypes);
    const userList = useSelector((state: RootState) => state.userManagemnetPageData.userList);
    const validateStatus = useSelector((state: RootState) => state.soilPageData.validateStatus);
    const userInfo = useSelector((state: RootState) => state.soilUser.userInfo);
    const {
        actionType,
        name,
        data,
        selectObjectType,
        objectID,
        desc,
        selectFieldType,
        isStandard,
        placeName,
        selectPublisher,
        ecoSystemMethods,
        profileid,
        fieldTypes,
        status
    } = useSelector((state: RootState) => state.soilprofileCreateData);

    // SECTION: define the function
    const showErrorMessage = (warningString: string) => {
        confirm({
            title: 'Warning',
            content: warningString,
            className: 'soil-profile-warning-div',
            onOk() {
                console.log('OK');
            },
            onCancel() {
                console.log('Cancel');
            },
        });
    };

    const handleSave = async () => {
        try {
            // once save! turn on the field required checked.
            dispatch(updateTheFieldRequiredCheckModel(true));
            // avoid request for save multiple times
            if (saveLock === true) {
                return;
            }

            let layerNameValidate = true;
            let methodValidate = true;
            let startDepthValidate = true;
            let endDepthValidate = true;

            const layerDetails = data.map((item) => {
                if (!item.layer) {
                    layerNameValidate = false;
                }

                if (item.startDepth < 0) {
                    startDepthValidate = false;
                }

                if (item.endDepth <= item.startDepth || item.endDepth === 0) {
                    endDepthValidate = false;
                }

                if (!item.method) {
                    methodValidate = false;
                }
                return {
                    layerID: item.layerID,
                    layer: item.layer,
                    // have no use but must be pass a number
                    rank: item.rank,
                    thickness: '0',
                    startDepth: item.startDepth,
                    endDepth: item.endDepth,
                    method: item.method && item.method.propertyMeasurementID
                };
            });

            if (!layerNameValidate) {
                return showErrorMessage('layer can\' be null!');
            }

            if (!startDepthValidate ) {
                return showErrorMessage('start depth can\' be null!');
            }

            // if (!thickenessValidate) {
            //     return showErrorMessage('thickness can\' be null!');
            // }
            if (!endDepthValidate) {
                return showErrorMessage('end depth must be less than start depth or can\'t be zero!');
            }

            if (!methodValidate) {
                return showErrorMessage('method can\' be null!');
            }

            if (!name) {
                return showErrorMessage('profile name can\'t be none!');
            }

            if (!placeName) {
                return showErrorMessage('coordinate can\' be null!');
            }
            const defaultUserArr = userList.filter((item) => item.userId === userInfo.userId);
            const requestParam: ISoilProfileAddProfileReqModel = {
                profileid,
                addEditFlag: actionType,
                profileName: name,
                profileDesc: desc,
                lat: placeName.lat,
                lng: placeName.lng,
                fieldType: selectFieldType,
                publisher: selectPublisher.userId || defaultUserArr[0].userId,
                layerDetails,
                isStandard,
                extrefID: selectObjectType.externalReferenceId,
                extObject: objectID,
                placeName: placeName.name,
                countrycode: placeName.countryCode,
                adminname1: placeName.adminName1
            };
            dispatch(updateShowLoading(true));
            const result = await soilProfileAddNewProfile(requestParam);
            if (result) {
                setCurrentPage('0');
                dispatch(resetTheSoilCreatProfiles());
                if(actionType=='add')
                    dispatch(updateSuccessMessage(`New Soil Profile ${name} has been added`));
                if(actionType=='edit')
                    dispatch(updateSuccessMessage(`Soil Profile ${name} has been Updated`));
                dispatch(updateShowLoading(false));
                dispatch(updateCoordinate(undefined));
                setSaveLock(false);
                addSuccessCallBack();
            }
        } catch(err) {
            console.log('err:==', err);
            dispatch(updateShowLoading(false));
        }
    };

    const handleObjectIdChange = (e) => {
        const { value: inputValue } = e.target;
        // const reg = /^-?\d*(\.\d*)?$/;
        // reg.test(inputValue)
        dispatch(updateObjectID(inputValue));
    };

    const checkTheRequireIssue = () => {
        let layerNameValidate = true;
        // let thickenessValidate = true;
        let methodValidate = true;
        let nameValidate = true;
        let coordinateValidate = true;
        let startDepthValidate = true;
        let endDepthValidate = true;

        data.forEach((item) => {
            if (!item.layer) {
                layerNameValidate = false;
            }
            if (!item.startDepth) {
                startDepthValidate = false;
            }
            if (!item.endDepth || item.endDepth < item.startDepth) {
                endDepthValidate = false;
            }
            if (!item.method) {
                methodValidate = false;
            }
        });

        if (!name) {
            nameValidate = false;
        }

        if (!placeName) {
            coordinateValidate = false;
        }

        if (nameValidate && coordinateValidate && layerNameValidate && methodValidate && endDepthValidate && startDepthValidate) {
            return setFieldCheckResult('all required field is fullfield');
        }

        const validateNotice = `
        ${nameValidate ? '' : 'name '}
        ${coordinateValidate ? '' : 'coordinate '}
        ${layerNameValidate ? '' : 'layer-name '}
        ${startDepthValidate ? '' : 'start-depth '}
        ${endDepthValidate ? '' : 'end-depth '}
        ${methodValidate ? '' : 'estimation-method '} field is required`;

        const noticeStr = titleCase(validateNotice);
        setFieldCheckResult(noticeStr);
    };

    const handlePublisherSelect = (index) => {
        const publisher= userList[index];
        dispatch(updateSelectPublisher(publisher));
    };

    const handlePublisherSelectObj = (item: IUserManagementSearchModel) => {
        dispatch(updateSelectPublisher(item));
    };

    const handleSourceLinkSelect = (index) => {
        const source = ecoSystemMethods[index].externalReferenceSystem;
        dispatch(updateSelectEstimationSystem(source));
        dispatch(updateSelectObjectType({
            externalReferenceId: 0,
            externalReferenceSystem: '',
            externalReferenceObject: '',
            externalReferenceDescription: ''}));
    };

    const handleObjectTypeSelect = (index) => {
        dispatch(updateSelectObjectType(objectTypes[index]));
    };

    const tabChange = (e: string) => {
        setCurrentPage(e);
    };

    // SECTION: define the useCallback
    const getObjectTypes = useCallback(async () => {
        try {
            const response = await soilProfileGetExtObjects({ extSystem: selectEcoSystem });
            dispatch(updateObjectType(response));
        } catch(err) {
            console.log('err', err);
        }
    }, [selectEcoSystem]);

    const getFieldType = useCallback(async () => {
        try {
            if (!fieldTypes.length) {
                const response = await soilProfileGetFieldType();
                response.reverse();
                dispatch(updateFieldTypes(response));
            }
        } catch (err) {
            console.log('err', err);
        }
    }, []);

    const getUserLists = useCallback(async () => {
        try {
            const response = await getUserList({isGetAllUsers: true});
            dispatch(getUserListAction({
                userList: response, downloadUserLists: [], deleteFileBoolean: true, searchUserInformations: [],
                addUserList: [], sendWelcomeEmailList: [], updateUserList:[]}));
        } catch(err) {
            console.log('err', err);
        }
    }, []);

    // request for source link and method list
    const getEstimationMethodList = useCallback(async () => {
        try {
            const response = await soilProfileGetExistSourceLink();
            dispatch(updateEcoSystems(response));
            const methodList = await soilProfileGetMethodList();
            dispatch(updateEstimationMethods(methodList));
        } catch (err) {
            console.log('err', err);
        }
    }, []);

    const getValidateStatus = useCallback(async () => {
        try {
            if (validateStatus.length === 0) {
                const validateStatus = await soilProfileGetValiadateStatus();
                dispatch(updateValidateStatus(validateStatus));
            }
        } catch(err) {
            console.log('err', err);
        }
    }, []);

    const handleCancelButtonClick = () => {
        confirm({
            title: 'Warning',
            icon: <WarningOutlined style={{color:'#E99921'}}/>,
            content: 'Unsaved changes will be lost. Do you want to exit without saving?',
            okText: 'Exit without saving',
            okType: 'danger',
            className: 'modal-global-radius',
            cancelText: 'Stay on page',
            onOk() {
                // if quit the edit or add page modal reset the soilpage model
                dispatch(resetTheSoilCreatProfiles());
                handleCancel();
            },
            onCancel() {
                // do nothing
                console.log('Cancel');
            },
        });
    };

    // SECTION: UseEffect area
    useEffect(() => {
        if (selectEcoSystem && isAuthenticate) {
            getObjectTypes();
        }
    }, [selectEcoSystem, isAuthenticate]);

    useEffect(() => {
        const newObjTypes = objectTypes.map((item) => item.externalReferenceObject);
        setObjTypes(newObjTypes);
    }, [objectTypes]);
    useEffect(() => {

        if (!userDisplayNameList.length) {
            const result = userList.map((Item) => Item.userDisplayName);
            setUserDisplayNameList(result);
        }


        if (selectPublisher.roleId === 0 && userInfo) {
            const defaultUserArr = userList.filter((item) => item.userId === userInfo.userId);
            if (defaultUserArr && defaultUserArr.length > 0) {
                dispatch(updateSelectPublisher(defaultUserArr[0]));
            }
        }
    }, [userList, userInfo]);

    useEffect( () => {
        checkTheRequireIssue();
    }, [name, placeName, data]);

    useEffect(() => {
        const result = ecoSystemMethods.map((item) => item.externalReferenceSystem);
        setEstimationMethodsDisplayName(result);
    }, [ecoSystemMethods]);

    useEffect(() => {
        getUserLists();
        getFieldType();
        getEstimationMethodList();
        getValidateStatus();
    }, []);

    const items = [
        { label: 'Soil Profile Details',
            key: '0',
            children: (
                <div className='tabs-container'>
                    <AddNewSoilProfile />
                </div>
            ) },
        { label: 'Reference To External System',
            key: '1',
            style: { minHeight: '100%' },
            children: (
                <div className='tabs-container'>
                    <div className='referene-div-first'>
                        <SoilDropDown
                            title='Source'
                            defaultText={'Select'}
                            selectors={estimationMethodsDisplayName}
                            selectValueCallback={(value, index) => handleSourceLinkSelect(index)}
                            value={selectEcoSystem}
                        />
                    </div>
                    <div className='referene-div'>
                        <SoilDropDown
                            enable={!!objectTypes.length}
                            defaultText={'Select'}
                            title='Object Type'
                            selectors={objTypes}
                            selectValueCallback={(value, index) => handleObjectTypeSelect(index)}
                            value={selectObjectType.externalReferenceObject}
                        />
                    </div>
                    <div className='referene-div-input'>
                        <label>Object ID</label>
                        <Input
                            className='object-id-input'
                            onChange={handleObjectIdChange}
                            value={objectID}
                        />
                    </div>
                </div>
            )},
        { label: 'Provider',
            key: '2',
            style: { minHeight: '100%' },
            children: (
                <div className='tabs-container'>
                    <div className='referene-tab-container'>
                        <SoilDropDown
                            style={{maxHeight: '300px', overflow: 'auto'}}
                            title='Publisher'
                            selectorsObj={userList}
                            defaultText='Select'
                            selectValueCallback={(value, index) => handlePublisherSelect(index)}
                            selectValueObjCallback={(value) => handlePublisherSelectObj(value) }
                            value={selectPublisher.userDisplayName}
                            type={'input-dropdown'}
                        />
                    </div>
                    <div className='object-id-referene-tab-container'>
                        <label>Status</label>
                        <label>{status || 'Draft'}</label>
                        {/* <SoilDropDown
                                style={{maxHeight: '300px', overflow: 'auto'}}
                                title='Status'
                                selectors={validateStatusList}
                                defaultText='Draft'
                                selectValueCallback={(value, index) => handlePublisherSelect(index)}
                                value={selectPublisher.userDisplayName}
                            /> */}
                    </div>
                </div>
            ) },
    ];

    // SECTION: render the view
    return (
        <Modal
            className='add-new-profile-modal'
            open={visible}
            centered
            width='950px'
            style={{
                borderRadius: '16px',
                borderWidth: '1px',
                borderStyle: 'solid',
                overflow: 'hidden',
                background: 'white',
                borderColor: 'white',
            }}
            title={<h3 className="profile-header">{actionType === 'add' ? 'New' : 'Edit'} Soil Profile</h3>}
            onCancel={() => {
                handleCancelButtonClick();
            }}
            closeIcon={<SoilCloseIcon />}
            footer={[
                <span
                    key='test'
                    style={{
                        float: 'left',
                        fontFamily: 'Noto-Sans-Regular',
                        fontStyle: 'normal',
                        fontWeight: '400',
                        fontSize: '12px',
                        height: '34px',
                        lineHeight: '34px',
                        textAlign: 'center',
                        letterSpacing: '-0.0075em',
                    }}>
                        Soil profile will be saved with the Draft status.
                </span>,
                <SoilButton
                    className='soil-profile-save-button'
                    style={{ marginRight: '10px' }}
                    highLight='default'
                    type='always'
                    key="close"
                    onClick={handleCancelButtonClick}
                >
                    Cancel
                </SoilButton>,
                <Tooltip
                    mouseEnterDelay={2}
                    key='tooltip-notice'
                    title={fieldCheckResult}
                >
                    <Button className='soil-profile-save-button' key="submit" type="primary" onClick={handleSave}>
                         Save
                    </Button>
                </Tooltip>
            ]}
        >
            <Tabs
                activeKey={currentPage}
                onChange={tabChange}
                style={{ width: '100%', marginTop: '-20px', minHeight: '100%' }}
                items={items}
            />
            {/* <TabPane tab='Soil Profile Details' key={0}>
                    <div className='tabs-container'>
                        <AddNewSoilProfile />
                    </div>
                </TabPane>
                <TabPane tab='Reference To External System' key={1} style={{ minHeight: '100%' }}>
                    <div className='tabs-container'>
                        <div className='referene-div-first'>
                            <SoilDropDown
                                title='Source'
                                defaultText={'Select'}
                                selectors={estimationMethodsDisplayName}
                                selectValueCallback={(value, index) => handleSourceLinkSelect(index)}
                                value={selectEcoSystem}
                            />
                        </div>
                        <div className='referene-div'>
                            <SoilDropDown
                                enable={!!objectTypes.length}
                                defaultText={'Select'}
                                title='Object Type'
                                selectors={objectTypes.map((item) => item.externalReferenceObject)}
                                selectValueCallback={(value, index) => handleObjectTypeSelect(index)}
                                value={selectObjectType.externalReferenceObject}
                            />
                        </div>
                        <div className='referene-div-input'>
                            <label>Object ID</label>
                            <Input
                                className='object-id-input'
                                onChange={handleObjectIdChange}
                                value={objectID}
                            />
                        </div>
                    </div>
                </TabPane>
                <TabPane tab='Provider' key={2} style={{ minHeight: '100%' }}>
                    <div className='tabs-container'>
                        <div className='referene-tab-container'>
                            <SoilDropDown
                                style={{maxHeight: '300px', overflow: 'auto'}}
                                title='Publisher'
                                selectors={userDisplayNameList}
                                defaultText='Select'
                                selectValueCallback={(value, index) => handlePublisherSelect(index)}
                                value={selectPublisher.userDisplayName}
                            />
                        </div>
                        <div className='object-id-referene-tab-container'>
                            <label>Status</label>
                            <label>{status || 'Draft'}</label>
                        </div>
                    </div>
                </TabPane> */}
            {/* </Tabs> */}
        </Modal>
    );
}
